import { Component, OnInit, NgModule } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { RxwebValidators, RxReactiveFormsModule } from '@rxweb/reactive-form-validators';
import { IdentityUserExtnService } from '@proxy/identity-user-extn';
import { PublicService } from '@proxy/public';
import { MemberFormModel } from './member-form.model';
import { finalize } from 'rxjs';
import { ToasterService } from '@abp/ng.theme.shared';
import { MessageService } from 'primeng/api';
import { HostInfoDto } from '@proxy/shared';
import { CommonModule } from '@angular/common';
import { CoreModule } from '@abp/ng.core';
import { ThemeSharedModule } from '@abp/ng.theme.shared';
import { GooglePlacesModule } from '../shared/google-places/google-places.module';
import { GooglePlacesComponent } from '../shared/google-places/google-places.component';

@Component({
  selector: 'app-member-form',
  templateUrl: './member-form.component.html',
  styleUrls: ['./member-form.component.scss'],
  providers: [MessageService]
})
export class MemberFormComponent implements OnInit {
  form: FormGroup;
  isModalBusy = false;
  
  showGoogleAddress = true;
  duplicateUsers: any[] = [];
  
  userAdded = false;
  subscriptionAdded = false;
  emailSent = false;
  processFinished = false;
  
  memberData: MemberFormModel;
  isEditMode = false;
  
  constructor(
    private fb: FormBuilder,
    private publicService: PublicService,
    private service: IdentityUserExtnService,
    private dialogRef: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private toaster: ToasterService,
    private messageService: MessageService
  ) { }

  ngOnInit(): void {
    // Get data passed to the component
    this.memberData = this.config.data?.member || {};
    this.isEditMode = !!this.memberData.id;
    
    this.buildForm();
    
    // Check for duplicate users if in edit mode
    if (this.isEditMode && this.memberData.email) {
      this.service.getAllInvalidUsersByEmailAddresses([this.memberData.email]).subscribe(result => {
        this.duplicateUsers = result;
      });
    }
  }

  buildForm() {
    const {
      name,
      surname,
      email,
      phoneNumber,
      addressLine1,
      addressLine2,
      addressLine3,
      postalCode,
      extraProperties
    } = this.memberData;
    
    this.form = this.fb.group({
      name: [name ?? null, [Validators.required]],
      surname: [surname ?? null, [Validators.required]],
      email: [email ?? null, [Validators.required, Validators.email]],
      userName: [this.memberData?.email ?? null],
      phoneNumber: [phoneNumber ?? null, [Validators.required, RxwebValidators.mask({mask:'(999)-999 9999' })]],
      addressLine1: [(addressLine1 || extraProperties?.AddressLine1) ?? null, [Validators.required]],
      addressLine2: [(addressLine2 || extraProperties?.AddressLine2) ?? null, [Validators.required]],
      addressLine3: [(addressLine3 || extraProperties?.AddressLine3) ?? null, [Validators.required]],
      postalCode: [(postalCode || extraProperties?.PostalCode) ?? null, [Validators.required]]
    });
  }

  getAddress($event) {
    this.showGoogleAddress = false;

    let street_number = $event.address_components.filter(a => a.types.indexOf('street_number') > -1);
    let street_name = $event.address_components.filter(a => a.types.indexOf('route') > -1);
    let subpremise = $event.address_components.filter(a => a.types.indexOf('subpremise') > -1);
    
    let city = $event.address_components.filter(a => a.types.indexOf('locality') > -1);
    let province = $event.address_components.filter(a => a.types.indexOf('administrative_area_level_1') > -1);
    let postal_code = $event.address_components.filter(a => a.types.indexOf('postal_code') > -1);
    
    if (street_number.length) {
      let addressLine1 = street_number[0].short_name;
      if (street_name.length) {
        addressLine1 = addressLine1 + ' ' + street_name[0].short_name;
      }
      if (subpremise.length) {
        addressLine1 = addressLine1 + ' ' + subpremise[0].short_name;
      }
      this.form.get("addressLine1").patchValue(addressLine1);
    }

    if (city.length) {
      this.form.get("addressLine2").patchValue(city[0].short_name);
    }
    if (province.length) {
      this.form.get("addressLine3").patchValue(province[0].short_name);
    }
    if (postal_code.length) {
      this.form.get("postalCode").patchValue(postal_code[0].short_name);
    }
  }

  submitForm() {
    if (this.form.invalid) return;

    let formValues = this.form.value;
    
    formValues.extraProperties = {
      ...(this.memberData?.extraProperties || {}),
      AddressLine1: formValues.addressLine1,
      AddressLine2: formValues.addressLine2,
      AddressLine3: formValues.addressLine3,
      PostalCode: formValues.postalCode,
    };
    
    const createUser: HostInfoDto = {
      name: this.form.value.name,
      surName: this.form.value.surname,
      email: this.form.value.email,
      phoneNumber: this.form.value.phoneNumber,
      isMember: true,
      paid: false,
      legacyMembershipId: null,
      addressLine1: this.form.value.addressLine1,
      addressLine2: this.form.value.addressLine2,
      addressLine3: this.form.value.addressLine3,
      postalCode: this.form.value.postalCode
    };

    this.isModalBusy = true;
    const familyHostId = this.config.data?.familyHostId || 'Host';

    if (this.isEditMode) {
      this.publicService.updateUserByIdAndUser(this.memberData.id, {
        ...formValues,
      }).subscribe(
        result => {
          this.isModalBusy = false;
          this.dialogRef.close(result);
        }, 
        error => {
          this.isModalBusy = false;
          this.toaster.error("Error updating user. Please try again.");
        }
      );
    } else {
      this.userAdded = false;
      this.subscriptionAdded = false;
      this.emailSent = false;
      
      this.publicService.createUser(createUser, familyHostId).subscribe(result => {
        if (!result.id) {
          this.processFinished = true;
          this.userAdded = false;
          this.subscriptionAdded = false;
          this.emailSent = false;
          this.isModalBusy = false;
          return;
        }
        
        this.userAdded = true;
        this.publicService.addSubscriptionsByNameAndSurnameAndEmailAndPhone(
          createUser.name, createUser.surName, createUser.email, createUser.phoneNumber
        ).subscribe(resp => {
          if (!resp) {
            this.processFinished = true;
            this.isModalBusy = false;
            return;
          }
          
          this.subscriptionAdded = true;
          this.publicService.sendWelcomeEmailById(result.id).subscribe(resp1 => {
            if (!resp1) {
              this.emailSent = false;
              this.processFinished = true;
              this.isModalBusy = false;
              return;
            }
            
            this.emailSent = true;
            this.processFinished = true;
            this.isModalBusy = false;
            
            // Wait a moment to show the success messages before closing
            setTimeout(() => {
              this.dialogRef.close(result);
            }, 1500);
          });
        });
      });
    }
  }

  cancel() {
    this.dialogRef.close();
  }
}

@NgModule({
  declarations: [MemberFormComponent],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RxReactiveFormsModule,
    CoreModule,
    ThemeSharedModule,
    GooglePlacesModule
  ],
  exports: [MemberFormComponent]
})
export class MemberFormModule {}
